import { doc, setDoc, getDoc, updateDoc, onSnapshot } from 'firebase/firestore';
import { db } from './firebase';
import { Session, Restaurant, CuisineFilter } from './types';
import { generateSessionCode } from './utils';

export const createSession = async (
  name: string,
  zipCode: string,
  restaurantCount: 10 | 20 | 30,
  restaurants: Restaurant[],
  cuisineFilter: CuisineFilter,
  excludeFastFood: boolean,
): Promise<string> => {
  const sessionCode = generateSessionCode();
  const now = Date.now();

  const session: Session = {
    sessionCode,
    createdAt: now,
    expiresAt: now + 2 * 60 * 60 * 1000,
    restaurantCount,
    restaurants,
    userA: {
      name,
      swipes: {},
      finished: false,
    },
    userB: null,
    matches: [],
    status: 'waiting',
    zipCode,
    cuisineFilter,
    excludeFastFood,
  };

  await setDoc(doc(db, 'sessions', sessionCode), session);
  return sessionCode;
};

export const joinSession = async (
  sessionCode: string,
  name: string
): Promise<Session> => {
  const sessionRef = doc(db, 'sessions', sessionCode.toUpperCase());
  const sessionSnap = await getDoc(sessionRef);

  if (!sessionSnap.exists()) {
    throw new Error('Session not found. Check your code and try again.');
  }

  const session = sessionSnap.data() as Session;

  if (session.status === 'finished') {
    throw new Error('This session has already finished.');
  }

  if (Date.now() > session.expiresAt) {
    throw new Error('This session has expired.');
  }

  if (session.userB !== null) {
    throw new Error('Someone has already joined this session.');
  }

  await updateDoc(sessionRef, {
    userB: {
      name,
      swipes: {},
      finished: false,
    },
    status: 'active',
  });

  return { ...session, userB: { name, swipes: {}, finished: false }, status: 'active' };
};

export const recordSwipe = async (
  sessionCode: string,
  user: 'userA' | 'userB',
  restaurantId: string,
  direction: 'yes' | 'no',
): Promise<void> => {
  const sessionRef = doc(db, 'sessions', sessionCode);

  // Always fetch fresh state before recording swipe
  const freshSnap = await getDoc(sessionRef);
  if (!freshSnap.exists()) return;
  const freshSession = freshSnap.data() as Session;

  const updatedSwipes = {
    ...freshSession[user]!.swipes,
    [restaurantId]: direction,
  };

  const otherUser = user === 'userA' ? 'userB' : 'userA';
  const otherSwipes = freshSession[otherUser]?.swipes || {};

  const newMatches = [...freshSession.matches];
  if (direction === 'yes' && otherSwipes[restaurantId] === 'yes') {
    if (!newMatches.includes(restaurantId)) {
      newMatches.push(restaurantId);
    }
  }

  await updateDoc(sessionRef, {
    [`${user}.swipes`]: updatedSwipes,
    matches: newMatches,
  });
};

export const markUserFinished = async (
  sessionCode: string,
  user: 'userA' | 'userB',
): Promise<void> => {
  const sessionRef = doc(db, 'sessions', sessionCode);

  await updateDoc(sessionRef, {
    [`${user}.finished`]: true,
  });

  // Re-fetch fresh session state to check if both are done
  const freshSnap = await getDoc(sessionRef);
  if (!freshSnap.exists()) return;

  const freshSession = freshSnap.data() as Session;
  const otherUser = user === 'userA' ? 'userB' : 'userA';
  const otherFinished = freshSession[otherUser]?.finished ?? false;

  if (otherFinished) {
    await updateDoc(sessionRef, { status: 'finished' });
  }
};

export const subscribeToSession = (
  sessionCode: string,
  callback: (session: Session) => void
): (() => void) => {
  const sessionRef = doc(db, 'sessions', sessionCode);
  return onSnapshot(sessionRef, (snap) => {
    if (snap.exists()) {
      callback(snap.data() as Session);
    }
  });
};